import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, apiFetchBlob } from '@/lib/api/client';
import { isJudgeParty, useSession } from '@/lib/auth/session';
import type {
  DailyLog,
  DailyLogInput,
  DailyLogSubmitResult,
  EquipmentDailyLog,
  HomeGreeting,
  Inspection,
  LaborRecord,
  Member,
  Ncr,
  PaymentClaim,
  PortalDocument,
  PortalPhoto,
  ProgressPreview,
  PurchaseOrder,
  QualityTestReport,
  RfqIntent,
  SafetyEducation,
  SafetyInspection,
  ShopDrawing,
  SiteContact,
  SiteHome,
  SiteOverview,
  SiteProgress,
  SiteVendor,
  SiteWeather,
  SiteWorker,
  Subcontract,
  SupervisionLog,
  Tbm,
  Uuid,
  VendorRfqDetail,
  VendorRfqRow,
} from '@/lib/types';

/*
  쿼리 키를 한곳에 모은다. 흩어 놓으면 「저장했는데 목록이 안 바뀌는」 버그가 나는데,
  그때 원인이 키 오타라는 것은 한참 뒤에야 보인다.
*/
export const keys = {
  subcontracts: (siteId: Uuid) => ['site', siteId, 'subcontracts'] as const,
  claims: (siteId: Uuid) => ['site', siteId, 'claims'] as const,
  labor: (siteId: Uuid) => ['site', siteId, 'labor'] as const,
  dailyLogs: (siteId: Uuid) => ['site', siteId, 'daily-logs'] as const,
  inspections: (siteId: Uuid) => ['site', siteId, 'inspections'] as const,
  inspection: (id: Uuid) => ['inspection', id] as const,
  documents: (siteId: Uuid) => ['site', siteId, 'documents'] as const,
  members: () => ['members'] as const,
  safetyEducations: (siteId: Uuid) => ['site', siteId, 'safety-educations'] as const,
  tbm: (siteId: Uuid) => ['site', siteId, 'tbm'] as const,
  workers: (siteId: Uuid) => ['site', siteId, 'workers'] as const,
  drawings: (siteId: Uuid) => ['site', siteId, 'drawings'] as const,
  supervisionLogs: (siteId: Uuid) => ['site', siteId, 'supervision-logs'] as const,
  safetyInspections: (siteId: Uuid) => ['site', siteId, 'safety-inspections'] as const,
  ncrs: (siteId: Uuid) => ['site', siteId, 'ncrs'] as const,
  testReports: (siteId: Uuid) => ['site', siteId, 'test-reports'] as const,
  purchaseOrders: (siteId: Uuid) => ['site', siteId, 'purchase-orders'] as const,
  equipmentLogs: (siteId: Uuid) => ['site', siteId, 'equipment-logs'] as const,
  // 견적·입찰은 현장 아래가 아니다 — 공고가 현장을 들고 있고 여러 현장 것을 한 목록에서 본다.
  rfqs: () => ['rfqs'] as const,
  rfq: (id: Uuid) => ['rfq', id] as const,
  siteVendors: (siteId: Uuid) => ['site', siteId, 'vendors'] as const,
  siteOverview: (siteId: Uuid) => ['site', siteId, 'overview'] as const,
  siteContacts: (siteId: Uuid) => ['site', siteId, 'contacts'] as const,
  siteProgress: (siteId: Uuid) => ['site', siteId, 'progress'] as const,
};

/**
 * 이 현장의 협력사 — 안전 지적과 NCR 의 <b>수신 업체</b>를 고르는 목록이다.
 *
 * <p>고르지 않고 발행하면 {@code vendor_id} 가 비어 어느 협력사 화면에도 뜨지 않는다. 그래서
 * 두 폼에서 필수이고, 그 필수를 세우려면 목록이 먼저 있어야 한다.
 *
 * <p><b>판정하는 손(감리·직원)일 때만 묻는다.</b> 서버가 협력사에게는 403 으로 답하므로(그 현장의
 * 다른 협력사 이름이 담긴다), 부를 수 없는 것을 부르고 실패를 세지 않는다.
 */
export function useSiteVendors(siteId: Uuid) {
  const session = useSession();
  return useQuery({
    queryKey: keys.siteVendors(siteId),
    queryFn: () => apiFetch<SiteVendor[]>(`/sites/${siteId}/vendors`),
    enabled: siteId !== '' && session.data !== undefined && isJudgeParty(session.data.partyType),
    staleTime: 5 * 60_000,
  });
}

// ── 현장의 현재 상태 — 개요 · 연락처 · 공정 ─────────────────────────────────

/**
 * 현장 개요.
 *
 * <p>서버가 <b>없는 현장에 빈 값</b>으로 답할 수 있어(`findOverview` 는 한 줄을 찾는다) 타입에
 * null 을 담는다 — 그때 화면은 오류가 아니라 「없음」으로 읽어야 한다.
 *
 * <p>현장 기준자료는 하루에 몇 번씩 바뀌는 값이 아니라 오래 묵힌다.
 */
export function useSiteOverview(siteId: Uuid) {
  return useQuery({
    queryKey: keys.siteOverview(siteId),
    queryFn: () => apiFetch<SiteOverview | null>(`/sites/${siteId}/overview`),
    staleTime: 5 * 60_000,
  });
}

/** 현장 연락처. 타 협력사를 빼는 일은 <b>서버가 이미 했다</b> — 여기서 또 거르지 않는다. */
export function useSiteContacts(siteId: Uuid) {
  return useQuery({
    queryKey: keys.siteContacts(siteId),
    queryFn: () => apiFetch<SiteContact[]>(`/sites/${siteId}/contacts`),
    staleTime: 5 * 60_000,
  });
}

/** 공정. 각 줄의 최신 실적까지 온다 — 「지금 몇 %인가」에 답하는 값이다. */
export function useSiteProgress(siteId: Uuid) {
  return useQuery({
    queryKey: keys.siteProgress(siteId),
    queryFn: () => apiFetch<SiteProgress[]>(`/sites/${siteId}/progress`),
  });
}

export function useSubcontracts(siteId: Uuid) {
  return useQuery({
    queryKey: keys.subcontracts(siteId),
    queryFn: () => apiFetch<Subcontract[]>(`/sites/${siteId}/subcontracts`),
  });
}

export function useClaims(siteId: Uuid) {
  return useQuery({
    queryKey: keys.claims(siteId),
    queryFn: () => apiFetch<PaymentClaim[]>(`/sites/${siteId}/claims`),
  });
}

export function useCreateClaim(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { periodYm: string; claimAmount: number; note: string | null }) =>
      apiFetch<PaymentClaim>(`/sites/${siteId}/claims`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.claims(siteId) }),
  });
}

export function useLabor(siteId: Uuid) {
  return useQuery({
    queryKey: keys.labor(siteId),
    queryFn: () => apiFetch<LaborRecord[]>(`/sites/${siteId}/labor`),
  });
}

export function useCreateLabor(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { logDate: string; trade: string | null; headcount: number; workArea: string | null }) =>
      apiFetch<LaborRecord>(`/sites/${siteId}/labor`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.labor(siteId) }),
  });
}

export function useDailyLogs(siteId: Uuid) {
  return useQuery({
    queryKey: keys.dailyLogs(siteId),
    queryFn: () => apiFetch<DailyLog[]>(`/sites/${siteId}/daily-logs`),
  });
}

/*
  ── 작업일보 쓰기 — 직원만 (DOC-87 §3-5 · 결정 ⑥) ─────────────────────────────
  협력사는 여전히 조회만이다. 서버가 `requireStaff()` 로 막는다 — 화면은 버튼을 세울지만 정한다.
  다섯 경로(작성·수정·제출·제출취소·프리뷰)와 날씨는 전부 같은 목록 하나를 움직여 한 자리에 둔다.
*/
export function useDailyLogActions(siteId: Uuid) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.dailyLogs(siteId) });

  /** 없으면 만들고 있으면 고친다 — 현장당 하루 한 장이라(`uq_pmis_daily_logs_site_date`) id 로 갈린다. */
  const save = useMutation({
    mutationFn: ({ id, ...body }: DailyLogInput & { id: Uuid | null }) =>
      id === null
        ? apiFetch<DailyLog>(`/sites/${siteId}/daily-logs`, { method: 'POST', body })
        : apiFetch<DailyLog>(`/sites/${siteId}/daily-logs/${id}`, { method: 'PUT', body }),
    onSuccess: invalidate,
  });

  /**
   * 제출 — 이 순간 실적이 공정율 스냅샷으로 접힌다(`DailyLogProgressService.recalculate`).
   * 수기 진행율과 충돌한 공정은 결과의 `skipped` 로 돌아온다 — 화면이 그대로 말해 준다.
   */
  const submit = useMutation({
    mutationFn: ({ id, overwriteManual }: { id: Uuid; overwriteManual: boolean }) =>
      apiFetch<DailyLogSubmitResult>(`/sites/${siteId}/daily-logs/${id}/submit`, { method: 'POST', body: { overwriteManual } }),
    onSuccess: invalidate,
  });

  const unsubmit = useMutation({
    mutationFn: (id: Uuid) => apiFetch<DailyLog>(`/sites/${siteId}/daily-logs/${id}/unsubmit`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  return { save, submit, unsubmit };
}

/**
 * 제출 프리뷰 — 「이 일보를 반영하면 무엇이 얼마가 되는가」. 아무것도 쓰지 않는다.
 * 제출 모달이 열릴 때만 묻는다(`id` 가 null 이면 끈다).
 */
export function useDailyLogProgressPreview(siteId: Uuid, id: Uuid | null) {
  return useQuery({
    queryKey: ['site', siteId, 'daily-logs', id, 'progress-preview'] as const,
    queryFn: () => apiFetch<ProgressPreview>(`/sites/${siteId}/daily-logs/${id}/progress-preview`),
    enabled: id !== null,
    staleTime: 0,
  });
}

/**
 * 일보 칸에 미리 채울 관측 날씨. 좌표는 요청이 아니라 `ref_sites` 에서 온다(서버 규칙).
 * 사람이 「날씨 가져오기」를 눌렀을 때만 묻는다 — 폼을 열 때마다 바깥 API 를 두드리지 않는다.
 */
export function useSiteWeather(siteId: Uuid) {
  return useMutation({
    mutationFn: (date: string) =>
      apiFetch<SiteWeather>(`/sites/${siteId}/daily-logs/weather?date=${encodeURIComponent(date)}`),
  });
}

/**
 * 목록 훅의 선택지. 사이드바 배지({@code useTodoCounts})가 현장이 없을 때 묻지 않으려고 쓴다 —
 * 훅은 조건부로 부를 수 없어 {@code enabled} 로 끈다. 화면은 이 인자를 주지 않는다.
 */
type ListOptions = { enabled?: boolean };

export function useInspections(siteId: Uuid, options: ListOptions = {}) {
  return useQuery({
    queryKey: keys.inspections(siteId),
    queryFn: () => apiFetch<Inspection[]>(`/sites/${siteId}/inspections`),
    enabled: options.enabled ?? true,
  });
}

export function useInspection(id: Uuid) {
  return useQuery({
    queryKey: keys.inspection(id),
    queryFn: () => apiFetch<Inspection>(`/inspections/${id}`),
  });
}

export function useCreateInspection(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; inspectionType: string; trade: string | null; location: string | null; dueDate: string | null }) =>
      apiFetch<Inspection>(`/sites/${siteId}/inspections`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.inspections(siteId) }),
  });
}

/**
 * 검측 판정 · 착수 · 재검.
 *
 * <p>세 동작 모두 목록과 상세를 함께 무르게 한다 — 재검은 <b>새 행을 만들기</b> 때문에
 * 상세만 갱신하면 목록에 새 회차가 나타나지 않는다.
 */
export function useInspectionAction(siteId: Uuid, id: Uuid) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: keys.inspections(siteId) });
    void queryClient.invalidateQueries({ queryKey: keys.inspection(id) });
  };

  const start = useMutation({
    mutationFn: () => apiFetch<Inspection>(`/inspections/${id}/start`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const judge = useMutation({
    mutationFn: (body: { status: 'PASSED' | 'FAILED'; resultNote: string; defectType: string | null }) =>
      apiFetch<Inspection>(`/inspections/${id}/verdict`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const recheck = useMutation({
    mutationFn: () => apiFetch<Inspection>(`/inspections/${id}/recheck`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  return { start, judge, recheck };
}

/**
 * 검측 보고서(PDF) — 회차별 사진·지적·판정 한 장(DOC-87 §3-7). 협력사·감리·직원 셋 다 같은 경로다.
 * 인쇄가 아니라 내려받기다 — 보고 있던 탭 그대로 파일이 내려온다.
 */
export function useDownloadInspectionReport() {
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: Uuid; fileName: string }) => {
      const blob = await apiFetchBlob(`/inspections/${id}/report`);
      saveBlob(blob, fileName);
    },
  });
}

export function useDocuments(siteId: Uuid) {
  return useQuery({
    queryKey: keys.documents(siteId),
    queryFn: () => apiFetch<PortalDocument[]>(`/sites/${siteId}/documents`),
  });
}

/** 열람 증적. 문서를 여는 순간 서버가 「누가 언제」 를 남긴다(DOC-82 §7). */
export function useMarkDocumentViewed(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: Uuid) => apiFetch<PortalDocument>(`/documents/${documentId}/view`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.documents(siteId) }),
  });
}

export function useMembers() {
  return useQuery({
    queryKey: keys.members(),
    queryFn: () => apiFetch<Member[]>('/members'),
  });
}

// ── 안전 ─────────────────────────────────────────────────────────────────────

export function useSafetyEducations(siteId: Uuid) {
  return useQuery({
    queryKey: keys.safetyEducations(siteId),
    queryFn: () => apiFetch<SafetyEducation[]>(`/sites/${siteId}/safety-educations`),
  });
}

export function useTbm(siteId: Uuid, options: ListOptions = {}) {
  return useQuery({
    queryKey: keys.tbm(siteId),
    queryFn: () => apiFetch<Tbm[]>(`/sites/${siteId}/tbm`),
    enabled: options.enabled ?? true,
  });
}

/**
 * TBM 서명.
 *
 * <p>서명은 <b>사람마다 한 번</b>이다. 두 번 눌러도 하나여야 하므로 서버가 멱등으로 받는다 —
 * 현장에서 버튼이 눌렸는지 확신이 안 서 다시 누르는 일은 늘 있다.
 */
export function useSignTbm(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tbmId: Uuid) => apiFetch<Tbm>(`/tbm/${tbmId}/sign`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.tbm(siteId) }),
  });
}

// ── 투입 인력 ────────────────────────────────────────────────────────────────

export function useWorkers(siteId: Uuid, options: ListOptions = {}) {
  return useQuery({
    queryKey: keys.workers(siteId),
    queryFn: () => apiFetch<SiteWorker[]>(`/sites/${siteId}/workers`),
    enabled: options.enabled ?? true,
  });
}

export function useCreateWorker(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      birthDate: string | null;
      phone: string | null;
      trade: string | null;
      jobTitle: string | null;
      enteredOn: string;
      safetyEducated: boolean;
      healthChecked: boolean;
    }) => apiFetch<SiteWorker>(`/sites/${siteId}/workers`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.workers(siteId) }),
  });
}

// ── 시공도·자재 승인 ─────────────────────────────────────────────────────────

export function useDrawings(siteId: Uuid) {
  return useQuery({
    queryKey: keys.drawings(siteId),
    queryFn: () => apiFetch<ShopDrawing[]>(`/sites/${siteId}/drawings`),
  });
}

export function useSubmitDrawing(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { drawingNo: string; title: string; revision: string; kind: string; trade: string | null }) =>
      apiFetch<ShopDrawing>(`/sites/${siteId}/drawings`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.drawings(siteId) }),
  });
}

export function useReviewDrawing(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, comment }: { id: Uuid; status: 'APPROVED' | 'REJECTED'; comment: string }) =>
      apiFetch<ShopDrawing>(`/drawings/${id}/review`, { method: 'POST', body: { status, comment } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.drawings(siteId) }),
  });
}

// ── 감리일지 ─────────────────────────────────────────────────────────────────

export function useSupervisionLogs(siteId: Uuid) {
  return useQuery({
    queryKey: keys.supervisionLogs(siteId),
    queryFn: () => apiFetch<SupervisionLog[]>(`/sites/${siteId}/supervision-logs`),
  });
}

export function useCreateSupervisionLog(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      logDate: string;
      weather: string | null;
      progressNote: string | null;
      instruction: string | null;
      remark: string | null;
      /** 수신 업체. null 이면 현장의 모든 협력사가 본다(기본). */
      targetVendorId: Uuid | null;
    }) => apiFetch<SupervisionLog>(`/sites/${siteId}/supervision-logs`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.supervisionLogs(siteId) }),
  });
}

// ── 안전 점검·시정 지시 ──────────────────────────────────────────────────────

export function useSafetyInspections(siteId: Uuid, options: ListOptions = {}) {
  return useQuery({
    queryKey: keys.safetyInspections(siteId),
    queryFn: () => apiFetch<SafetyInspection[]>(`/sites/${siteId}/safety-inspections`),
    enabled: options.enabled ?? true,
  });
}

/**
 * 점검 회차 등록 · 지적 추가 · 조치 회신 · 확인.
 *
 * <p>넷이 <b>같은 목록 하나</b>를 움직인다 — 지적은 회차에 매달려 오기 때문에, 조치 회신 한 건도
 * 결국 회차 목록을 다시 받아야 화면이 맞는다. 그래서 훅을 넷으로 흩지 않고 한 자리에 묶었다.
 */
export function useSafetyInspectionActions(siteId: Uuid) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.safetyInspections(siteId) });

  const createRound = useMutation({
    mutationFn: (body: { inspectedOn: string; inspectionKind: string }) =>
      apiFetch<SafetyInspection>(`/sites/${siteId}/safety-inspections`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  /** {@code vendorId} 는 <b>누구에게 준 지적인가</b>다. 비면 직영분이 되어 아무에게도 안 간다. */
  const addFinding = useMutation({
    mutationFn: ({ inspectionId, ...body }: { inspectionId: Uuid; finding: string; location: string | null; riskLevel: string; dueDate: string; vendorId: Uuid }) =>
      apiFetch<SafetyInspection>(`/safety-inspections/${inspectionId}/actions`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const respond = useMutation({
    mutationFn: ({ actionId, actionNote }: { actionId: Uuid; actionNote: string }) =>
      apiFetch<SafetyInspection>(`/safety-actions/${actionId}/response`, { method: 'POST', body: { actionNote } }),
    onSuccess: invalidate,
  });

  const confirm = useMutation({
    mutationFn: (actionId: Uuid) =>
      apiFetch<SafetyInspection>(`/safety-actions/${actionId}/confirm`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  return { createRound, addFinding, respond, confirm };
}

// ── NCR(부적합) ──────────────────────────────────────────────────────────────

export function useNcrs(siteId: Uuid, options: ListOptions = {}) {
  return useQuery({
    queryKey: keys.ncrs(siteId),
    queryFn: () => apiFetch<Ncr[]>(`/sites/${siteId}/ncrs`),
    enabled: options.enabled ?? true,
  });
}

/**
 * 발행 · 회신 · 판정.
 *
 * <p>발행과 판정은 감리가, 회신은 협력사가 부른다. 서버가 주체를 보고 막지만 화면도 같은 선을
 * 긋는다 — 버튼이 있는데 403 이 나는 것보다 버튼이 없는 편이 낫다.
 */
export function useNcrActions(siteId: Uuid) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.ncrs(siteId) });

  /** {@code vendorId} 는 지적과 같은 자리다 — 받는 업체가 없으면 발행이 기록으로만 남는다. */
  const issue = useMutation({
    mutationFn: (body: { title: string; trade: string | null; description: string; dueDate: string | null; vendorId: Uuid }) =>
      apiFetch<Ncr>(`/sites/${siteId}/ncrs`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const respond = useMutation({
    mutationFn: ({ id, ...body }: { id: Uuid; cause: string; correctiveAction: string; preventiveAction: string }) =>
      apiFetch<Ncr>(`/ncrs/${id}/response`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const review = useMutation({
    mutationFn: ({ id, status }: { id: Uuid; status: '조치확인' | '종결' }) =>
      apiFetch<Ncr>(`/ncrs/${id}/review`, { method: 'POST', body: { status } }),
    onSuccess: invalidate,
  });

  return { issue, respond, review };
}

// ── 시험성적서 ───────────────────────────────────────────────────────────────

export function useTestReports(siteId: Uuid) {
  return useQuery({
    queryKey: keys.testReports(siteId),
    queryFn: () => apiFetch<QualityTestReport[]>(`/sites/${siteId}/test-reports`),
  });
}

export function useCreateTestReport(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      testType: string;
      target: string;
      sampledOn: string;
      testedOn: string | null;
      agency: string | null;
      specValue: string;
      resultValue: string | null;
    }) => apiFetch<QualityTestReport>(`/sites/${siteId}/test-reports`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.testReports(siteId) }),
  });
}

/** 합부 판정. 결과값을 적은 쪽(협력사)이 아니라 감리가 누른다. */
export function useJudgeTestReport(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, verdict }: { id: Uuid; verdict: '합격' | '불합격' }) =>
      apiFetch<QualityTestReport>(`/test-reports/${id}/verdict`, { method: 'POST', body: { verdict } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.testReports(siteId) }),
  });
}

// ── 구매 발주·납품·검수 ──────────────────────────────────────────────────────

export function usePurchaseOrders(siteId: Uuid) {
  return useQuery({
    queryKey: keys.purchaseOrders(siteId),
    queryFn: () => apiFetch<PurchaseOrder[]>(`/sites/${siteId}/purchase-orders`),
  });
}

/**
 * 납품 등록과 검수.
 *
 * <p>발주를 만드는 훅은 <b>없다</b> — PO 는 본사가 낸다(DOC-82 §2 3차). 납품은 협력사가 올리고
 * 검수는 감리가 누른다. 둘을 한 주체가 하면 「내가 넣고 내가 합격」이 된다.
 */
export function usePurchaseActions(siteId: Uuid) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.purchaseOrders(siteId) });

  const deliver = useMutation({
    mutationFn: ({ orderId, ...body }: { orderId: Uuid; deliveredOn: string; quantity: number; invoiceNo: string | null }) =>
      apiFetch<PurchaseOrder>(`/purchase-orders/${orderId}/deliveries`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const inspect = useMutation({
    mutationFn: ({ deliveryId, ...body }: { deliveryId: Uuid; result: '합격' | '불합격'; note: string | null }) =>
      apiFetch<PurchaseOrder>(`/deliveries/${deliveryId}/inspect`, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  return { deliver, inspect };
}

// ── 장비 가동일보 ────────────────────────────────────────────────────────────

export function useEquipmentLogs(siteId: Uuid) {
  return useQuery({
    queryKey: keys.equipmentLogs(siteId),
    queryFn: () => apiFetch<EquipmentDailyLog[]>(`/sites/${siteId}/equipment-logs`),
  });
}

export function useCreateEquipmentLog(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      logDate: string;
      equipmentName: string;
      spec: string | null;
      operatingHours: number;
      fuelLiters: number | null;
      operatorName: string | null;
      workContent: string | null;
    }) => apiFetch<EquipmentDailyLog>(`/sites/${siteId}/equipment-logs`, { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.equipmentLogs(siteId) }),
  });
}

/** 가동 확인. 이 값이 대여료·유류비의 근거라 적은 쪽이 스스로 확인하지 않는다. */
export function useConfirmEquipmentLog(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => apiFetch<EquipmentDailyLog>(`/equipment-logs/${id}/confirm`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.equipmentLogs(siteId) }),
  });
}

// ── 사진 · 파일 (DOC-84 §3-D) ───────────────────────────────────────────────

/** 사진이 붙는 부모 — 경로 마디이자 서버의 `ExternalPhotoService.Target` 이다. */
export type PhotoTarget = 'inspections' | 'daily-logs' | 'ncrs';

export function usePhotos(target: PhotoTarget, parentId: Uuid) {
  return useQuery({
    queryKey: ['photos', target, parentId] as const,
    queryFn: () => apiFetch<PortalPhoto[]>(`/${target}/${parentId}/photos`),
  });
}

/**
 * 사진 올리기 — multipart 로 서버를 거친다(presign 을 밖에 주지 않는다). 성공하면 그 부모의 사진 목록을
 * 턴다. 부모 목록의 `photoCount` 는 화면이 `onUploaded` 로 따로 턴다 — 어느 키인지는 화면이 안다.
 */
export function useUploadPhoto(target: PhotoTarget, parentId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return apiFetch<PortalPhoto>(`/${target}/${parentId}/photos`, { method: 'POST', body });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['photos', target, parentId] }),
  });
}

/**
 * 문서 내려받기 — 열람 증적을 먼저 남기고(`POST /view`) 파일을 받는다. 인쇄가 아니라 다운로드다.
 *
 * <p>증적이 먼저다. 바이트가 늦게 와도 「열었다」는 사실은 남아야 하고, 실패한 내려받기도 열람 시도다.
 */
export function useDownloadDocument(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: Uuid; fileName: string }) => {
      await apiFetch<PortalDocument>(`/documents/${id}/view`, { method: 'POST' });
      const blob = await apiFetchBlob(`/documents/${id}/file`);
      saveBlob(blob, fileName);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.documents(siteId) }),
  });
}

/** 받은 바이트를 파일로 내린다 — 보고 있던 탭 그대로. */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // 클릭이 처리된 뒤에 지운다 — 곧바로 지우면 일부 브라우저가 빈 파일을 내린다.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * 홈 인사말의 「오늘의 한 마디」 — 답해야 닫히는 것 중 가장 급한 하나(DOC-84 §7 ⑥).
 *
 * <p>실패해도 홈은 뜬다. 인사말은 재촉하는 장치이지 인가가 아니라, 못 받으면 이름과 날짜만 보인다.
 */
export function useSiteGreeting(siteId: Uuid) {
  return useQuery({
    queryKey: ['site', siteId, 'greeting'] as const,
    queryFn: () => apiFetch<HomeGreeting>(`/sites/${siteId}/greeting`),
    staleTime: 60_000,
    retry: false,
  });
}

// ── 견적·입찰 (DOC-98 §5-1) ─────────────────────────────────────────────────

/** 초청받은 공고. 초청 밖은 <b>있는지조차</b> 오지 않는다(지명경쟁 · 결정 ⑦). */
export function useRfqs() {
  return useQuery({
    queryKey: keys.rfqs(),
    queryFn: () => apiFetch<VendorRfqRow[]>('/rfqs'),
  });
}

/**
 * 공고 한 건.
 *
 * <p><b>여는 것이 곧 증적이다</b> — 서버가 그 순간 「누가 언제 봤나」를 찍는다(`markInviteViewed`).
 * 그래서 묵히지 않는다. 캐시로 조용히 넘기면 두 번째부터는 본 기록이 남지 않는다.
 */
export function useRfq(id: Uuid) {
  return useQuery({
    queryKey: keys.rfq(id),
    queryFn: () => apiFetch<VendorRfqDetail>(`/rfqs/${id}`),
    staleTime: 0,
  });
}

/**
 * 참가 의사 · 응찰 · 철회.
 *
 * <p>셋 다 <b>상세를 통째로</b> 돌려준다. 받은 값을 그대로 캐시에 앉히면 다시 묻지 않아도 되고,
 * 그만큼 열람 증적이 한 번 더 찍히는 일도 없다. 목록은 제출 여부가 바뀌므로 무르게만 한다.
 *
 * <p>막는 것(마감·상태)은 <b>서버가 판정한다</b> — 화면은 버튼을 세울지만 정하고, 늦게 눌린 것은
 * 서버가 거절해 한 문장으로 돌아온다.
 */
export function useRfqActions(id: Uuid) {
  const queryClient = useQueryClient();
  const apply = (detail: VendorRfqDetail) => {
    queryClient.setQueryData(keys.rfq(id), detail);
    void queryClient.invalidateQueries({ queryKey: keys.rfqs() });
  };

  const intent = useMutation({
    mutationFn: (value: RfqIntent) =>
      apiFetch<VendorRfqDetail>(`/rfqs/${id}/intent`, { method: 'POST', body: { intent: value } }),
    onSuccess: apply,
  });

  /** 마감 전이면 다시 보내 고칠 수 있다 — 서버가 같은 줄을 고친다. */
  const bid = useMutation({
    mutationFn: (body: { amount: number; vatIncluded: boolean; note: string | null }) =>
      apiFetch<VendorRfqDetail>(`/rfqs/${id}/bid`, { method: 'POST', body }),
    onSuccess: apply,
  });

  const withdraw = useMutation({
    mutationFn: () => apiFetch<VendorRfqDetail>(`/rfqs/${id}/bid/withdraw`, { method: 'POST' }),
    onSuccess: apply,
  });

  return { intent, bid, withdraw };
}

/** 홈 머리. 개요·공정·사진을 한 번에 받는다 — 조각조각 채워지지 않게. */
export function useSiteHome(siteId: Uuid) {
  return useQuery({
    queryKey: ['site', siteId, 'home'] as const,
    queryFn: () => apiFetch<SiteHome>(`/sites/${siteId}/home`),
    staleTime: 60_000,
  });
}
